import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { GenericOption } from '../../../voxel/Forms/VoxelAutocomplete';
import VoxelInput from '../../../voxel/Forms/VoxelInput';
import VoxelMenuItemContainer from '../../../voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelTooltipShort from '../../../voxel/Tooltip/VoxelTooltipShort';
import { HeadCellImplementationProps } from '../KeywordTable/HeadCells/models';
import HeadCell from './HeadCell';
import HeadCellSelectOption from './HeadCellSelectOption';
import HeadCellStandardOptions from './HeadCellStandardOptions';

export interface HeadCellSelectProps extends HeadCellImplementationProps {
  state?: any[];
  setState?: (state: GenericOption[]) => void;
  options: GenericOption[];
  align?: 'left' | 'right' | 'center';
}

const HeadCellSelect: React.FC<HeadCellSelectProps> = ({
  state,
  setState,
  options,
  basicConfig,
  basicState,
  align = 'left',
}) => {
  const handleFilterChange = useCallback(
    (selectedOption: GenericOption, checked: boolean) => {
      const newState = [...(state ?? [])].filter((opt) => !(opt === selectedOption.value));

      checked && newState.push(selectedOption.value);
      setState && setState(newState);
    },
    [state]
  );

  const selectAllOptions = useCallback(() => {
    setState && setState(options.map((opt) => opt.value));
  }, [state]);

  const resetFilter = useCallback(() => {
    setState && setState([]);
  }, [state]);

  // Option search
  const [optionSearchText, setOptionSearchText] = useState('');
  const displayedOptions = useMemo(() => {
    if (!optionSearchText.length) {
      return options;
    }
    const words = optionSearchText.split(' ').map((word) => word.toLowerCase());
    return options.filter((option) =>
      words.every((word) => option.title.toLowerCase().includes(word))
    );
  }, [optionSearchText]);

  return (
    <HeadCell
      hasFilter={!!state?.length}
      align={align}
      basicConfig={basicConfig}
      basicState={basicState}
    >
      <VoxelMenuItemContainer size="md">
        <HeadCellStandardOptions basicConfig={basicConfig} basicState={basicState} />
        <div className="p-2 flex flex-col gap-2">
          <div className="flex flex-row items-start gap-2">
            <Link
              component="button"
              variant="caption"
              underline="always"
              onClick={selectAllOptions}
              disabled={state?.length === options.length}
              className="!underline !decoration-1 disabled:!no-underline disabled:text-gray-400  disabled:cursor-default"
            >
              Select all
            </Link>
            <VoxelTooltipShort message="clear selection" disabled={!state?.length}>
              <Link
                component="button"
                variant="caption"
                underline="always"
                onClick={resetFilter}
                disabled={!state?.length}
                className="!underline !decoration-1 disabled:!no-underline disabled:text-gray-400  disabled:cursor-default"
              >
                Clear
              </Link>
            </VoxelTooltipShort>
          </div>
          <VoxelInput
            autoFocus
            size={'sm'}
            onChange={(event) => setOptionSearchText(event.target.value)}
            value={optionSearchText}
            startIcon={<FontAwesomeIcon icon={faSearch} />}
          />
        </div>
        {displayedOptions.map((option) => (
          <HeadCellSelectOption
            key={option.value}
            option={option}
            handleFilterChange={handleFilterChange}
            state={state}
          />
        ))}
      </VoxelMenuItemContainer>
    </HeadCell>
  );
};

export default HeadCellSelect;
